import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

export type UserRole = 'admin' | 'manager' | 'captain' | 'client' | 'guest';

export interface RoleCheckResult {
  hasRole: boolean;
  role: UserRole | null;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class RoleService {
  private userRole = new BehaviorSubject<UserRole | null>(null);
  public userRole$ = this.userRole.asObservable();

  constructor(private firestore: Firestore) {}

  /**
   * Set the current user's role
   * @param role The user's role (admin, manager, captain, client, guest)
   */
  setUserRole(role: UserRole): void {
    console.log('[RoleService] Setting user role to:', role);
    this.userRole.next(role);
  }

  /**
   * Get the current user's role
   * @returns The current user's role or null if not set
   */
  getUserRole(): UserRole | null {
    return this.userRole.getValue();
  }

  /**
   * Get the current user's role as an Observable
   * @returns Observable of the user's role
   */
  getUserRole$(): Observable<UserRole | null> {
    return this.userRole$;
  }

  /**
   * Check if user has a specific role
   * @param role The role to check for
   * @returns true if user has the role, false otherwise
   */
  hasRole(role: UserRole): boolean {
    const currentRole = this.userRole.getValue();
    return currentRole === role;
  }

  /**
   * Check if user has any of the specified roles
   * @param roles Array of roles to check
   * @returns true if user has any of the specified roles
   */
  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = this.userRole.getValue();
    return roles.includes(currentRole as UserRole);
  }

  /**
   * Check if user has all of the specified roles
   * @param roles Array of roles to check
   * @returns true if user has all of the specified roles
   */
  hasAllRoles(roles: UserRole[]): boolean {
    // Note: Users typically have one role at a time in most applications
    // This method is here for completeness and edge cases
    const currentRole = this.userRole.getValue();
    return roles.length === 0 || roles.includes(currentRole as UserRole);
  }

  /**
   * Check if user is an admin
   * @returns true if user has admin role
   */
  isAdmin(): boolean {
    return this.hasRole('admin');
  }

  /**
   * Check if user is a manager
   * @returns true if user has manager role
   */
  isManager(): boolean {
    return this.hasRole('manager');
  }

  /**
   * Check if user is a captain/employee
   * @returns true if user has captain role
   */
  isCaptain(): boolean {
    return this.hasRole('captain');
  }

  /**
   * Check if user is a client
   * @returns true if user has client role
   */
  isClient(): boolean {
    return this.hasRole('client');
  }

  /**
   * Check if user is a guest (unauthenticated)
   * @returns true if user has guest role
   */
  isGuest(): boolean {
    return this.hasRole('guest') || this.userRole.getValue() === null;
  }

  /**
   * Check if user can perform administrative actions
   * @returns true if user is admin or manager
   */
  canAdminister(): boolean {
    return this.hasAnyRole(['admin', 'manager']);
  }

  /**
   * Check if user can view sensitive data
   * @returns true if user is admin, manager, or captain
   */
  canViewSensitiveData(): boolean {
    return this.hasAnyRole(['admin', 'manager', 'captain']);
  }

  /**
   * Check if user can edit reports
   * @returns true if user is admin, manager, or captain
   */
  canEditReports(): boolean {
    return this.hasAnyRole(['admin', 'manager', 'captain']);
  }

  /**
   * Get the role priority/level
   * Higher number = higher privilege
   * @param role The role to get priority for
   * @returns Priority level (0-4)
   */
  getRolePriority(role: UserRole | null): number {
    switch (role) {
      case 'admin':
        return 4;
      case 'manager':
        return 3;
      case 'captain':
        return 2;
      case 'client':
        return 1;
      case 'guest':
      case null:
        return 0;
      default:
        return 0;
    }
  }

  /**
   * Get readable role name
   * @param role The role to get display name for
   * @returns Readable display name
   */
  getRoleDisplayName(role: UserRole | null): string {
    switch (role) {
      case 'admin':
        return 'Administrator';
      case 'manager':
        return 'Manager';
      case 'captain':
        return 'Captain';
      case 'client':
        return 'Client';
      case 'guest':
        return 'Guest';
      case null:
        return 'Not Authenticated';
      default:
        return 'Unknown';
    }
  }

  /**
   * Clear the user role
   */
  clearRole(): void {
    this.userRole.next(null);
  }

  // ============================================================================
  // EXTRACTED METHODS FROM AuthService AND UserService
  // These methods provide centralized role checking with Firestore queries
  // ============================================================================

  /**
   * Helper method to safely get user document data
   * Wraps getDoc to avoid injection context warnings
   */
  private async getUserDocData(userId: string): Promise<any> {
    try {
      const ref = doc(this.firestore, 'users', userId);
      const docSnap = await getDoc(ref);
      return docSnap.exists() ? docSnap.data() : null;
    } catch (error) {
      console.error('[RoleService] Error fetching user document:', error);
      return null;
    }
  }

  /**
   * Check if user has admin role (from Firestore)
   * Checks both role field and appRoles array
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is admin, false otherwise
   */
  async isUserAdmin(userId: string): Promise<boolean> {
    const data = await this.getUserDocData(userId);
    if (!data) {return false;}

    const hasRoleField = data.role === 'ADMIN' || data.role === 'admin';
    const hasAppRoles = Array.isArray(data.appRoles) && (data.appRoles.includes('ADMIN') || data.appRoles.includes('admin'));

    return hasRoleField || hasAppRoles;
  }

  /**
   * Check if user has manager role (from Firestore)
   * Checks both role field and appRoles array for MANAGER role
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is manager, false otherwise
   */
  async isUserManager(userId: string): Promise<boolean> {
    const data = await this.getUserDocData(userId);
    if (!data) {return false;}

    const hasRoleField = data.role === 'MANAGER' || data.role === 'manager';
    const hasAppRoles = Array.isArray(data.appRoles) && (data.appRoles.includes('MANAGER') || data.appRoles.includes('manager'));

    return hasRoleField || hasAppRoles;
  }

  /**
   * Check if user has client role (from Firestore)
   * Checks both role field and appRoles array for CLIENT role
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is client, false otherwise
   */
  async isUserClient(userId: string): Promise<boolean> {
    const data = await this.getUserDocData(userId);
    if (!data) {return false;}

    const hasRoleField = data.role === 'CLIENT' || data.role === 'client';
    const hasAppRoles = Array.isArray(data.appRoles) && (data.appRoles.includes('CLIENT') || data.appRoles.includes('client'));

    return hasRoleField || hasAppRoles;
  }

  /**
   * Check if user has tech role (from Firestore)
   * Checks both role field and appRoles array for TECH role
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is tech, false otherwise
   */
  async isUserTech(userId: string): Promise<boolean> {
    const data = await this.getUserDocData(userId);
    if (!data) {return false;}

    const hasRoleField = data.role === 'TECH' || data.role === 'tech';
    const hasAppRoles = Array.isArray(data.appRoles) && (data.appRoles.includes('TECH') || data.appRoles.includes('tech'));

    return hasRoleField || hasAppRoles;
  }

  /**
   * Check if user has mate role (from Firestore)
   * Checks both role field and appRoles array for MATE role
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is mate, false otherwise
   */
  async isUserMate(userId: string): Promise<boolean> {
    const data = await this.getUserDocData(userId);
    if (!data) {return false;}

    const hasRoleField = data.role === 'MATE' || data.role === 'mate';
    const hasAppRoles = Array.isArray(data.appRoles) && (data.appRoles.includes('MATE') || data.appRoles.includes('mate'));

    return hasRoleField || hasAppRoles;
  }

  /**
   * Check if user has inspector flag (from Firestore)
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is inspector, false otherwise
   */
  async isUserInspector(userId: string): Promise<boolean> {
    console.log('[RoleService] Checking for Inspector', userId);
    const data = await this.getUserDocData(userId);
    if (!data) {
      console.error('[RoleService] No Document Data found in isUserInspector');
      return false;
    }
    return data.inspector ?? false;
  }

  /**
   * Check if user has developer flag (from Firestore)
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user is developer, false otherwise
   */
  async isUserDeveloper(userId: string): Promise<boolean> {
    console.log('[RoleService] Checking for Developer', userId);
    const data = await this.getUserDocData(userId);
    if (!data) {
      console.error('[RoleService] No Document Data found in isUserDeveloper');
      return false;
    }
    return data.dev ?? false;
  }

  /**
   * Check if user has access to app (from Firestore)
   * User has access if role is USER, ADMIN, MATE, or TECH
   * @param userId The user's Firebase UID
   * @returns Promise<true> if user has access, false otherwise
   */
  async canUserAccess(userId: string): Promise<boolean> {
    console.log('[RoleService] Checking permissions for', userId);
    if (!userId) {
      return false;
    }

    const data = await this.getUserDocData(userId);
    if (!data) {
      return false;
    }

    const hasAccess = data.role === 'USER' || data.role === 'ADMIN' || data.role === 'MATE' || data.role === 'TECH';
    if (hasAccess) {
      console.debug('[RoleService] This user has a data role of :', data.role);
    } else {
      console.debug('[RoleService] User cannot access');
    }
    return hasAccess;
  }

  /**
   * Helper: Check if user data object has a specific role (no Firestore query)
   * Useful for checking roles in data already fetched from Firestore
   * @param data The user data object from Firestore
   * @param roleToCheck The role to check for
   * @returns true if user has the role, false otherwise
   */
  hasRoleInData(data: any, roleToCheck: string): boolean {
    if (!data) {
      return false;
    }
    return data.role === roleToCheck || (Array.isArray(data.appRoles) && data.appRoles.includes(roleToCheck));
  }
}
